import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PickemQuestion } from '@bet99-pickem/api-types';
import TeamPick from '@/components/game/TeamPick';
import PlayerPick from '@/components/game/PlayerPick';
import PropPick from '@/components/game/PropPick';
import TieBreakerQuestion from '@/components/game/TieBreakerQuestion';
import { QUESTION_TYPES } from '@/constants/question-types';
import { getTeamsForQuestion, getPercentagesForPropQuestion } from '@/utils/pickem-helpers';
import { getQuestionId } from '@/utils/id-helpers';
import { useLocalizedFieldGetter } from '@/utils/localized-field';

interface QuestionRendererProps {
  question: PickemQuestion;
  questionNumber?: number; // Question number for display (1-6), undefined for tiebreaker
  selectedTeams: Record<string, string>;
  selectedProps: Record<string, 'yes' | 'no'>;
  tieBreaker: string;
  onTeamSelect: (questionId: string, teamId: string | undefined) => void;
  onPropSelect: (questionId: string, answer: 'yes' | 'no') => void;
  onTieBreakerChange: (value: string) => void;
  isViewOnly: boolean;
  selectionResults?: Record<string, boolean>;
  selectionPoints?: Record<string, number>;
}

export const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  questionNumber,
  selectedTeams,
  selectedProps,
  tieBreaker,
  onTeamSelect,
  onPropSelect,
  onTieBreakerChange,
  isViewOnly,
  selectionResults,
  selectionPoints,
}) => {
  const { t } = useTranslation();
  const questionId = getQuestionId(question);
  const isCorrect = selectionResults?.[questionId];
  const earnedPoints = selectionPoints?.[questionId];
  const getField = useLocalizedFieldGetter();

  switch (question.question_type) {
    case QUESTION_TYPES.TIEBREAKER:
      return (
        <TieBreakerQuestion
          questionId={questionId}
          title={t('tiebreaker.title')}
          description={getField(question, 'description') || getField(question, 'title')}
          value={tieBreaker}
          onChange={onTieBreakerChange}
          disabled={isViewOnly}
          points={question.points || 0}
          earnedPoints={earnedPoints}
        />
      );

    case QUESTION_TYPES.TEAM: {
      const teams = getTeamsForQuestion(question);

      return (
        <TeamPick
          questionId={questionId}
          questionNumber={questionNumber}
          teams={teams}
          points={question.points || 0}
          questionText={getField(question, 'title')}
          earnedPoints={earnedPoints}
          selectedTeam={selectedTeams[questionId]}
          onTeamSelect={teamId => onTeamSelect(questionId, teamId)}
          isCorrect={isCorrect}
          disabled={isViewOnly}
        />
      );
    }

    case QUESTION_TYPES.PLAYER: {
      const teams = getTeamsForQuestion(question);

      return (
        <PlayerPick
          questionId={questionId}
          questionNumber={questionNumber}
          teams={teams}
          points={question.points || 0}
          earnedPoints={earnedPoints}
          questionText={getField(question, 'title')}
          selectedTeam={selectedTeams[questionId]}
          onTeamSelect={isViewOnly ? () => {} : teamId => onTeamSelect(questionId, teamId)}
          isCorrect={isCorrect}
          disabled={isViewOnly}
        />
      );
    }

    case QUESTION_TYPES.PROP: {
      const [team1, team2] = getTeamsForQuestion(question);
      const percentages = getPercentagesForPropQuestion(question);

      return (
        <PropPick
          questionId={questionId}
          questionNumber={questionNumber}
          teams={[team1, team2]}
          question={getField(question, 'title')}
          subtext={getField(question, 'description')}
          points={question.points || 0}
          earnedPoints={earnedPoints}
          percentages={percentages}
          selectedAnswer={selectedProps[questionId]}
          onAnswerSelect={isViewOnly ? () => {} : answer => onPropSelect(questionId, answer)}
          isCorrect={isCorrect}
          disabled={isViewOnly}
        />
      );
    }

    default:
      return null;
  }
};
